import { useState } from "react";
import { Link } from "react-router";
import { Button, Drawer } from "@heroui/react";
import { AnimatePresence, motion } from "framer-motion";
import {
    Bars3Icon,
    XMarkIcon,
    ChevronLeftIcon,
    RectangleStackIcon,
    StarIcon,
    BeakerIcon,
    PlusIcon,
    BookOpenIcon,
    SunIcon,
    MoonIcon,
    SwatchIcon,
    ArrowRightStartOnRectangleIcon
} from "@heroicons/react/24/outline";
import { signOut } from "@/services/auth";
import { useAuthContext } from "@/features/authentication/useAuthContext";
import { RecipeType, recipeTypeToPath } from "@/lib/types";

interface MobileMenuProps {
    theme: "light" | "dark";
    onToggleTheme: () => void;
}

type Chapter = "top" | "recipes";

export const MobileMenu = ({ theme, onToggleTheme }: MobileMenuProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const close = () => setIsOpen(false);

    return (
        <>
            <Button
                isIconOnly
                variant="ghost"
                size="sm"
                onPress={() => setIsOpen(true)}
                aria-label="Open menu"
                className="text-default-700 hover:text-foreground cursor-pointer"
            >
                <Bars3Icon className="w-6 h-6" />
            </Button>
            <Drawer.Backdrop isOpen={isOpen} onOpenChange={setIsOpen} variant="opaque">
                <Drawer.Content placement="right">
                    <Drawer.Dialog className="!w-screen !max-w-none !h-screen !max-h-none !rounded-none !p-0 !bg-[var(--color-secondary-500)] !shadow-none relative overflow-hidden text-default-50">
                        {/*
                          Decorative watermark layer goes here (sword + herb
                          marginalia, low-opacity SVGs in corners). Removed
                          pending an illustrator pass — the placeholder motifs
                          we authored were too literal and read as clutter.
                          See .spec/nav-redesign/design.md §13 for the motif
                          system the family should follow when reintroduced.
                        */}
                        <DrawerBody onClose={close} theme={theme} onToggleTheme={onToggleTheme} />
                    </Drawer.Dialog>
                </Drawer.Content>
            </Drawer.Backdrop>
        </>
    );
};

interface DrawerBodyProps {
    onClose: () => void;
    theme: "light" | "dark";
    onToggleTheme: () => void;
}

// Chapter state lives here so it resets naturally when the dialog unmounts on
// drawer close (react-aria's Modal removes the subtree after exit animation).
// Next open mounts a fresh DrawerBody starting at the top chapter — no manual
// reset, no animation-duration coupling.
const DrawerBody = ({ onClose, theme, onToggleTheme }: DrawerBodyProps) => {
    const [chapter, setChapter] = useState<Chapter>("top");
    const { user } = useAuthContext();
    const showDesignSystem = import.meta.env.DEV;

    return (
        <div className="relative h-full flex flex-col">
            <div className="px-4 h-16 flex items-center justify-between">
                <Link to="/" onClick={onClose} className="flex items-center gap-3 min-w-0">
                    <img src="/savorygrimoire-512.png" alt="" className="h-10 w-auto" />
                    <span className="font-display text-base tracking-[0.18em] uppercase text-default-50 max-w-min leading-tight sm:max-w-full sm:whitespace-nowrap sm:leading-normal">
                        The Savory Grimoire
                    </span>
                </Link>
                <Button
                    isIconOnly
                    variant="ghost"
                    size="sm"
                    onPress={onClose}
                    aria-label="Close menu"
                    className="text-default-100 hover:text-default-50 cursor-pointer"
                >
                    <XMarkIcon className="w-6 h-6" />
                </Button>
            </div>
            <div className="h-px mx-4 bg-gradient-to-r from-transparent via-accent-300/30 to-transparent" />

            <div className="flex-1 relative overflow-hidden">
                <AnimatePresence mode="wait" initial={false}>
                    {chapter === "top" ? (
                        <motion.div
                            key="top"
                            initial={{ opacity: 0, x: -16 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -16 }}
                            transition={{ duration: 0.14, ease: [0, 0, 0.2, 1] }}
                            className="absolute inset-0 flex flex-col items-center justify-start px-6 pt-12 pb-14 overflow-y-auto"
                        >
                            <TopChapter
                                onClose={onClose}
                                onOpenRecipes={() => setChapter("recipes")}
                                theme={theme}
                                onToggleTheme={onToggleTheme}
                                showDesignSystem={showDesignSystem}
                                userEmail={user?.email}
                            />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="recipes"
                            initial={{ opacity: 0, x: 16 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 16 }}
                            transition={{ duration: 0.14, ease: [0, 0, 0.2, 1] }}
                            className="absolute inset-0 flex flex-col items-center justify-start px-6 pt-12 pb-14 overflow-y-auto"
                        >
                            <RecipesChapter onBack={() => setChapter("top")} onClose={onClose} />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

interface TopChapterProps {
    onClose: () => void;
    onOpenRecipes: () => void;
    theme: "light" | "dark";
    onToggleTheme: () => void;
    showDesignSystem: boolean;
    userEmail: string | null | undefined;
}

const TopChapter = ({ onClose, onOpenRecipes, theme, onToggleTheme, showDesignSystem, userEmail }: TopChapterProps) => {
    const ThemeIcon = theme === "light" ? MoonIcon : SunIcon;
    return (
        <div className="w-full max-w-xs flex flex-col items-stretch flex-1">
            <div className="flex flex-col">
                <ChapterRow
                    as="button"
                    onClick={onOpenRecipes}
                    icon={<RectangleStackIcon className="w-5 h-5" />}
                    drillIn
                >
                    Recipes
                </ChapterRow>
                <ChapterRow
                    as="link"
                    to="/recipes?isFavorite=true"
                    onClick={onClose}
                    icon={<StarIcon className="w-5 h-5" />}
                >
                    Favorites
                </ChapterRow>
                <ChapterRow
                    as="link"
                    to="/recipes?hasMadeIt=false"
                    onClick={onClose}
                    icon={<BeakerIcon className="w-5 h-5" />}
                >
                    Not yet tried
                </ChapterRow>

                <Ornament />

                <ChapterRow as="link" to="/recipes/new" onClick={onClose} icon={<PlusIcon className="w-5 h-5" />}>
                    Add a recipe
                </ChapterRow>
                <ChapterRow as="link" to="/manage" onClick={onClose} icon={<BookOpenIcon className="w-5 h-5" />}>
                    Manage library
                </ChapterRow>
            </div>

            <div className="flex-1" aria-hidden="true" />

            <div className="flex flex-col">
                <Ornament />
                <ChapterRow as="button" onClick={onToggleTheme} icon={<ThemeIcon className="w-5 h-5" />}>
                    Switch to {theme === "light" ? "dark" : "light"} theme
                </ChapterRow>
                {showDesignSystem ? (
                    <ChapterRow
                        as="link"
                        to="/design-system"
                        onClick={onClose}
                        icon={<SwatchIcon className="w-5 h-5" />}
                    >
                        Design system
                    </ChapterRow>
                ) : null}
                <ChapterRow
                    as="button"
                    onClick={() => {
                        onClose();
                        void signOut();
                    }}
                    icon={<ArrowRightStartOnRectangleIcon className="w-5 h-5" />}
                    tone="muted"
                >
                    Sign out
                </ChapterRow>
                {userEmail ? (
                    <p className="mt-2 text-center font-EBGaramond text-base italic text-default-300">
                        signed in as {userEmail.split("@")[0]}
                    </p>
                ) : null}
            </div>
        </div>
    );
};

interface RecipesChapterProps {
    onBack: () => void;
    onClose: () => void;
}

const RecipesChapter = ({ onBack, onClose }: RecipesChapterProps) => (
    <div className="w-full max-w-xs flex flex-col items-stretch">
        <button
            type="button"
            onClick={onBack}
            className="self-center mb-3 inline-flex items-center gap-1.5 px-3 py-1.5 font-EBGaramond italic text-base text-default-200 hover:text-default-50 transition-colors cursor-pointer"
        >
            <ChevronLeftIcon className="w-4 h-4" />
            Back
        </button>

        <ChapterRow as="link" to="/recipes" onClick={onClose}>
            All recipes
        </ChapterRow>
        {Object.values(RecipeType).map((type) => (
            <ChapterRow key={type} as="link" to={`/recipes/type/${recipeTypeToPath(type)}`} onClick={onClose}>
                {type}
            </ChapterRow>
        ))}
    </div>
);

interface ChapterRowBaseProps {
    children: React.ReactNode;
    icon?: React.ReactNode;
    drillIn?: boolean;
    tone?: "default" | "muted";
}
type ChapterRowProps =
    | (ChapterRowBaseProps & { as: "link"; to: string; onClick?: () => void })
    | (ChapterRowBaseProps & { as: "button"; onClick: () => void });

const ChapterRow = (props: ChapterRowProps) => {
    const toneClass = props.tone === "muted" ? "text-default-300" : "text-default-50";
    const className = `flex w-full items-center justify-center gap-3 py-3 px-3 font-EBGaramond text-2xl ${toneClass} hover:text-accent-300 transition-colors cursor-pointer`;
    const content = (
        <>
            {props.icon ? <span className="text-default-300 shrink-0">{props.icon}</span> : null}
            <span>{props.children}</span>
            {props.drillIn ? (
                <span aria-hidden="true" className="text-default-300 shrink-0">
                    ›
                </span>
            ) : null}
        </>
    );
    if (props.as === "link") {
        return (
            <Link to={props.to} onClick={props.onClick} className={className}>
                {content}
            </Link>
        );
    }
    return (
        <button type="button" onClick={props.onClick} className={className}>
            {content}
        </button>
    );
};

const Ornament = () => (
    <div className="flex items-center justify-center my-3 select-none">
        <span className="text-accent-300/55 text-lg leading-none">❦</span>
    </div>
);
